/**
 * Spinner — the SystemDox loading indicator.
 *
 * Replaces the ad-hoc `<Loader2 className="animate-spin" />` scattered across
 * the app with a single branded mark: a comet ring sweeping the wordmark ramp
 * (blue → teal → emerald, matching SystemDoxHeader) and fading out into its own
 * tail.
 *
 * Built from a conic-gradient disc masked down to a ring rather than an SVG arc.
 * SVG has no angular gradient, so an arc can only carry a *linear* ramp across
 * its bounding box — which visibly clips one end of a three-stop brand ramp. The
 * conic gradient follows the sweep, so all three brand stops read at every size.
 * Consequence: this relies on `mask` (Safari 15.4+, matching the Tailwind v4
 * baseline the app already requires) and both the prefixed and unprefixed
 * properties are set.
 *
 * Colours are inline paint, not Tailwind classes, so the mark is unaffected by
 * how this package's sources are scanned for class extraction.
 */

import { cn } from '@systemdox/utils';

/** Diameter and ring thickness, in px. Thickness is tuned per size — a
 *  proportional stroke goes muddy at 16px and spindly at 40px. */
const SIZES = {
  sm: { px: 16, ring: 2 },
  md: { px: 24, ring: 2.5 },
  lg: { px: 40, ring: 4 },
} as const;

export type SpinnerSize = keyof typeof SIZES;

/** The wordmark ramp, as a full turn that fades into its own tail. The 0deg and
 *  360deg stops are the same hues at zero alpha so head and tail meet invisibly. */
const BRAND_SWEEP =
  'conic-gradient(from 0deg, rgba(37,99,235,0) 0deg, #2563eb 110deg, #14b8a6 230deg, #10b981 330deg, rgba(16,185,129,0) 360deg)';

/** The same sweep in the caller's own text colour. `color-mix` supplies the
 *  transparent stop rather than the `transparent` keyword, which resolves to
 *  transparent *black* and grey-fringes the tail on non-neutral colours. */
const CURRENT_SWEEP =
  'conic-gradient(from 0deg, color-mix(in srgb, currentColor 0%, transparent) 0deg, currentColor 300deg, color-mix(in srgb, currentColor 0%, transparent) 360deg)';

/**
 * The rule is about what the spinner sits *next to*, not only what it sits *on*:
 *
 * - `brand` (default) — page, panel and card backgrounds, and plain text rows.
 *   The mark is standing on its own, so it carries the brand.
 * - `current` — inside a button, and on coloured chips, tinted banners and any
 *   accent-filled surface. Two reasons. On a filled control the brand ramp fights
 *   its background (blue→teal→emerald on `bg-blue-600` loses its blue half and
 *   reads as a rendering fault). And in any button the spinner is paired with a
 *   label, usually replacing an icon — inheriting `currentColor` keeps the pair
 *   visually one unit.
 *
 * Shortcut: **inside a `<button>`, always `current`** — filled, outlined or ghost.
 * Everywhere else, `brand` unless the background is a solid or tinted accent.
 */
export type SpinnerTone = 'brand' | 'current';

export interface SpinnerProps {
  /** Diameter preset. `sm` inline/in-button, `md` inline sections, `lg` full-page. */
  size?: SpinnerSize;
  /** `brand` (default) on neutral surfaces; `current` on filled/coloured ones. */
  tone?: SpinnerTone;
  className?: string;
  /** Announced to assistive tech. Any visible label is the caller's to render. */
  'aria-label'?: string;
}

export function Spinner({
  size = 'md',
  tone = 'brand',
  className,
  'aria-label': ariaLabel = 'Loading',
}: SpinnerProps) {
  const { px, ring } = SIZES[size];
  const ringMask = `radial-gradient(farthest-side, transparent calc(100% - ${ring}px), #000 calc(100% - ${ring}px))`;

  return (
    <div
      role="status"
      aria-label={ariaLabel}
      style={{
        width: px,
        height: px,
        background: tone === 'current' ? CURRENT_SWEEP : BRAND_SWEEP,
        mask: ringMask,
        WebkitMask: ringMask,
      }}
      // Reduced motion keeps the ring turning — a frozen spinner reads as a hung
      // request — but well below the rate that provokes discomfort.
      className={cn('animate-spin rounded-full motion-reduce:[animation-duration:2.4s]', className)}
    />
  );
}

export interface LoadingStateProps {
  /** Visible copy under the mark, e.g. "Loading projects…". Omit for a bare ring. */
  label?: string;
  /** `md` for a section slot, `lg` for a whole page or panel. */
  size?: Extract<SpinnerSize, 'md' | 'lg'>;
  className?: string;
}

/**
 * The centred spinner-plus-caption block for wherever a page, panel or list is
 * waiting on its first payload.
 */
export function LoadingState({ label, size = 'md', className }: LoadingStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 text-slate-500 dark:text-slate-400',
        size === 'lg' ? 'py-16' : 'py-12',
        className,
      )}
    >
      <Spinner size={size} aria-label={label ?? 'Loading'} />
      {label && <p className="text-sm">{label}</p>}
    </div>
  );
}
